import { useEffect, useState, useCallback } from "react";
import { useAppStore } from "../../store/app";
import { readBestCvText, readProfileJson, writeProfileJson } from "../../lib/api/profile";
import { readAllWorkflows } from "../../lib/api/workflows";
import { api } from "../../lib/api/_client";
import { downloadBuffer } from "../../lib/browser/download";
import {
  consolidateProfile,
  computeConsolidationDiff,
  type ConsolidationSource,
} from "../../lib/ai/consolidation";
import { formatCV } from "../../lib/documents/cv-formatter";
import type { TailoredCVContent } from "../../lib/ai/document-gen";

interface SourceRow {
  vacancyId: string;
  vacancyTitle: string;
  generatedAt: string;
  selected: boolean;
  content: TailoredCVContent;
}

export default function ConsolidationScreen() {
  const language = useAppStore((s) => s.language);
  const repoPath = useAppStore((s) => s.repoPath);
  const activeCandidateFolder = useAppStore((s) => s.activeCandidateFolder);
  const settings = useAppStore((s) => s.settings);

  const [sources, setSources] = useState<SourceRow[]>([]);
  const [currentSystemMaster, setCurrentSystemMaster] = useState<TailoredCVContent | null>(null);
  const [consultantName, setConsultantName] = useState("");
  const [lastConsolidated, setLastConsolidated] = useState<string | null>(null);
  const [sourceCount, setSourceCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TailoredCVContent | null>(null);
  const [diff, setDiff] = useState<{ newCompetencies: number; newRoles: number; improvedProfile: boolean } | null>(null);
  const [saved, setSaved] = useState(false);

  const nl = language === "nl";

  const load = useCallback(async () => {
    if (!repoPath || !activeCandidateFolder) return;
    setLoading(true);
    try {
      const profile = await readProfileJson(repoPath, activeCandidateFolder);
      setConsultantName(profile?.personal?.name ?? activeCandidateFolder);
      setLastConsolidated(profile?.system_master_cv_last_consolidated ?? null);
      setSourceCount(profile?.consolidation_source_count ?? 0);

      // v2.0: system_master_cv lives on the consultant row directly.
      // The consultant record is `profile` in v1.7 terms but the whole row
      // is returned by the API — fetch it via api client.
      try {
        const consultantRow = await api<{ system_master_cv: TailoredCVContent | null }>(
          "GET",
          `/api/consultants/${encodeURIComponent(activeCandidateFolder)}`,
        );
        setCurrentSystemMaster(consultantRow.system_master_cv ?? null);
      } catch {
        setCurrentSystemMaster(null);
      }

      // v2.0: workflow tailored-CV JSON blobs are not yet persisted in Blob
      // Storage. Source list will be empty until Blob integration lands.
      // Kept as a no-op read so the workflows list still loads (may inform
      // the timestamps/count metadata below in a future revision).
      await readAllWorkflows(repoPath, activeCandidateFolder);
      setSources([]);
    } catch (err: any) {
      console.error("ConsolidationScreen load failed", err);
      setError(err?.message || "Failed to load consolidation data.");
    } finally {
      setLoading(false);
    }
  }, [repoPath, activeCandidateFolder]);

  useEffect(() => { void load(); }, [load]);

  function toggleSource(vacancyId: string) {
    setSources((prev) =>
      prev.map((s) => (s.vacancyId === vacancyId ? { ...s, selected: !s.selected } : s)),
    );
  }

  async function handleRun() {
    if (!repoPath || !activeCandidateFolder) return;
    const apiKey = settings?.anthropic_api_key || "";
    const model = settings?.anthropic_model || "gemini-2.5-flash";
    if (!apiKey) { setError(nl ? "Geen API-sleutel geconfigureerd." : "No API key configured."); return; }

    const selected = sources.filter((s) => s.selected);
    if (selected.length === 0) { setError(nl ? "Selecteer minimaal één bron." : "Select at least one source."); return; }

    setRunning(true);
    setError(null);
    setResult(null);
    setDiff(null);
    setSaved(false);

    try {
      const humanCvText = await readBestCvText(repoPath, activeCandidateFolder);
      const profile = await readProfileJson(repoPath, activeCandidateFolder);

      const consolidationSources: ConsolidationSource[] = selected.map((s) => ({
        vacancy_title: s.vacancyTitle,
        vacancy_id: s.vacancyId,
        generated_at: s.generatedAt,
        content: s.content,
      }));

      const consolidationResult = await consolidateProfile(
        apiKey,
        model,
        humanCvText,
        JSON.stringify(profile ?? {}),
        consolidationSources,
        currentSystemMaster,
        settings?.prompts?.consolidation,
      );

      setResult(consolidationResult.system_master);
      setDiff(computeConsolidationDiff(currentSystemMaster, consolidationResult.system_master));
    } catch (err: any) {
      console.error("Consolidation failed", err);
      setError(err?.message || "Consolidation failed.");
    } finally {
      setRunning(false);
    }
  }

  async function handleApprove() {
    if (!repoPath || !activeCandidateFolder || !result) return;
    setRunning(true);
    setError(null);
    try {
      // v2.0: system_master_cv persists on the consultant row via API PUT.
      // The .docx render is available as a browser download but is no
      // longer written to disk anywhere — Blob Storage integration pending.
      await api("PUT", `/api/consultants/${encodeURIComponent(activeCandidateFolder)}`, {
        system_master_cv: result,
      });

      // Update profile timestamps
      const profile = await readProfileJson(repoPath, activeCandidateFolder);
      if (profile) {
        const selected = sources.filter((s) => s.selected);
        profile.system_master_cv_last_consolidated = new Date().toISOString();
        profile.consolidation_source_count = (profile.consolidation_source_count ?? 0) + selected.length;
        await writeProfileJson(repoPath, activeCandidateFolder, profile);
        setLastConsolidated(profile.system_master_cv_last_consolidated);
        setSourceCount(profile.consolidation_source_count);
      }

      setCurrentSystemMaster(result);
      setSaved(true);
    } catch (err: any) {
      console.error("Approve consolidation failed", err);
      setError(err?.message || "Failed to save consolidation.");
    } finally {
      setRunning(false);
    }
  }

  async function handleDownloadDocx() {
    // v2.0: render on the fly from the current system_master_cv and stream
    // to the browser as a download. No persisted .docx blob (yet).
    const cv = result ?? currentSystemMaster;
    if (!cv) return;
    try {
      const docxBytes = await formatCV(cv, consultantName);
      downloadBuffer(
        docxBytes,
        `system_master_cv_${activeCandidateFolder ?? "consultant"}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      );
    } catch (err) {
      console.error("CV download failed", err);
    }
  }

  const selectedCount = sources.filter((s) => s.selected).length;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8 space-y-6 animate-in fade-in duration-200">
      {/* Header */}
      <div className="border-b border-slate-100 pb-4">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          {nl ? "Profiel Consolideren" : "Profile Consolidation"}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          {nl
            ? "Combineer alle op maat gemaakte CV's tot een rijkere System Master CV."
            : "Merge all tailored CVs into a richer System Master CV."}
        </p>
      </div>

      {/* Status card */}
      <div className="bg-white rounded-xl border border-slate-200/70 shadow-xs p-5 flex flex-wrap gap-6 items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            {nl ? "Consultant" : "Consultant"}
          </p>
          <p className="text-sm font-semibold text-slate-800">{consultantName}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            {nl ? "Laatste consolidatie" : "Last consolidated"}
          </p>
          <p className="text-sm font-semibold text-slate-800">
            {lastConsolidated
              ? new Date(lastConsolidated).toLocaleDateString(nl ? "nl-NL" : "en-GB", { day: "numeric", month: "long", year: "numeric" })
              : (nl ? "Nog nooit" : "Never")}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            {nl ? "Bronnen verwerkt" : "Sources processed"}
          </p>
          <p className="text-sm font-semibold text-slate-800">{sourceCount}</p>
        </div>
        {currentSystemMaster && (
          <button
            type="button"
            onClick={() => void handleDownloadDocx()}
            className="rounded-lg border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 text-xs font-semibold transition-all cursor-pointer"
          >
            {nl ? "Huidig .docx downloaden" : "Download current .docx"}
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="shrink-0 text-rose-400 hover:text-rose-600 cursor-pointer">✕</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500 mr-3" />
          <span className="text-sm">{nl ? "Laden…" : "Loading…"}</span>
        </div>
      ) : sources.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-10 text-center">
          <p className="text-sm font-medium text-slate-500">
            {nl
              ? "Nog geen op maat gemaakte CV's gevonden. Voltooi eerst een workflow."
              : "No tailored CVs found yet. Complete a workflow first."}
          </p>
        </div>
      ) : (
        <>
          {/* Source list */}
          <div className="bg-white rounded-xl border border-slate-200/70 shadow-xs overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">
                {nl ? "Beschikbare bronnen" : "Available sources"}{" "}
                <span className="font-normal text-slate-400">({sources.length})</span>
              </h2>
              <button
                type="button"
                className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                onClick={() => setSources((p) => p.map((s) => ({ ...s, selected: true })))}
              >
                {nl ? "Alles selecteren" : "Select all"}
              </button>
            </div>
            <ul className="divide-y divide-slate-100">
              {sources.map((src) => {
                const isNew = !lastConsolidated || new Date(src.generatedAt) > new Date(lastConsolidated);
                return (
                  <li key={src.vacancyId} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={src.selected}
                      onChange={() => toggleSource(src.vacancyId)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{src.vacancyTitle}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(src.generatedAt).toLocaleDateString(nl ? "nl-NL" : "en-GB", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </p>
                    </div>
                    {isNew && (
                      <span className="shrink-0 inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {nl ? "Nieuw" : "New"}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Run button */}
          {!result && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => void handleRun()}
                disabled={running || selectedCount === 0}
                className="rounded-lg bg-indigo-700 hover:bg-indigo-800 text-white px-5 py-2.5 text-sm font-semibold shadow-xs transition-all cursor-pointer disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {running ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-300 border-t-white" />
                    {nl ? "Bezig…" : "Running…"}
                  </>
                ) : (
                  nl
                    ? `Consolideer ${selectedCount} bron${selectedCount !== 1 ? "nen" : ""}`
                    : `Consolidate ${selectedCount} source${selectedCount !== 1 ? "s" : ""}`
                )}
              </button>
            </div>
          )}

          {/* Result diff + approve */}
          {result && diff && !saved && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-5 space-y-4">
              <h3 className="text-sm font-bold text-indigo-900">
                {nl ? "Consolidatie gereed — beoordeel de wijzigingen" : "Consolidation ready — review changes"}
              </h3>
              <div className="flex flex-wrap gap-4">
                {diff.newCompetencies > 0 && (
                  <div className="rounded-lg bg-white border border-indigo-100 px-4 py-2 text-center">
                    <p className="text-xl font-extrabold text-indigo-700">{diff.newCompetencies}</p>
                    <p className="text-[10px] text-indigo-500 font-semibold uppercase tracking-wider mt-0.5">
                      {nl ? "Nieuwe competenties" : "New competencies"}
                    </p>
                  </div>
                )}
                {diff.newRoles > 0 && (
                  <div className="rounded-lg bg-white border border-indigo-100 px-4 py-2 text-center">
                    <p className="text-xl font-extrabold text-indigo-700">{diff.newRoles}</p>
                    <p className="text-[10px] text-indigo-500 font-semibold uppercase tracking-wider mt-0.5">
                      {nl ? "Nieuwe rollen" : "New roles"}
                    </p>
                  </div>
                )}
                {diff.improvedProfile && (
                  <div className="rounded-lg bg-white border border-indigo-100 px-4 py-2 text-center">
                    <p className="text-xl font-extrabold text-indigo-700">✓</p>
                    <p className="text-[10px] text-indigo-500 font-semibold uppercase tracking-wider mt-0.5">
                      {nl ? "Profiel bijgewerkt" : "Profile updated"}
                    </p>
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg border border-indigo-100 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  {nl ? "Nieuwe profieltitel" : "New role title"}
                </p>
                <p className="text-sm font-semibold text-slate-800">{result.role_title}</p>
                {result.tailoring_notes && (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-3 mb-1">
                      {nl ? "Consolidatienotities" : "Consolidation notes"}
                    </p>
                    <p className="text-xs text-slate-600 leading-relaxed">{result.tailoring_notes}</p>
                  </>
                )}
              </div>
              <div className="flex gap-3 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => { setResult(null); setDiff(null); }}
                  className="rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 px-4 py-2 text-xs font-semibold cursor-pointer transition-colors"
                >
                  {nl ? "Verwerpen" : "Discard"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleApprove()}
                  disabled={running}
                  className="rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold shadow-xs cursor-pointer transition-all disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {running ? (
                    <><span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-300 border-t-white" />{nl ? "Opslaan…" : "Saving…"}</>
                  ) : (
                    nl ? "Goedkeuren & Opslaan" : "Approve & Save"
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Saved confirmation */}
          {saved && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
              <span className="text-emerald-500 text-lg mt-0.5">✓</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-emerald-800">
                  {nl ? "System Master CV bijgewerkt" : "System Master CV updated"}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {nl
                    ? "system_master_cv.json en system_master_cv.docx zijn opgeslagen."
                    : "system_master_cv.json and system_master_cv.docx have been saved."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleDownloadDocx()}
                className="shrink-0 rounded-lg border border-emerald-200 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 text-xs font-semibold cursor-pointer transition-colors"
              >
                {nl ? "Downloaden" : "Download"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
